'use client';

import { type JSX, useState } from 'react';

import { CardLayer, type Card } from './CardLayer';

const ASSET_BASE = '/assets/video-generation';

// The design frame is the 420x132 Figma frame that the cards are drawn in.
const DESIGN_FRAME = { width: 420, height: 132 } as const;
const OUTER_CARD = { width: 77.3, height: 79.98, top: 40, axisOffset: 140 } as const;
const INNER_CARD = { width: 122.92, height: 97.64, top: 17, axisOffset: 65 } as const;
const CENTER_CARD = { width: 122.92, height: 126.34, top: 0, axisOffset: 0 } as const;

// Cards declared in visual left-to-right order; stacking is controlled by zIndex
const CARDS: Card[] = [
    {
        ...OUTER_CARD,
        src: `${ASSET_BASE}/sand_dunes.webp`,
        alt: 'Sand dunes',
        side: -1,
        zIndex: 1,
        resting: { x: '0cqw', y: '0cqw', rotate: -14.39 },
        hover: { x: '-3.333cqw', y: '0.714cqw', rotate: -16.89 },
    },
    {
        ...INNER_CARD,
        src: `${ASSET_BASE}/polar_fox.webp`,
        alt: 'Polar fox',
        side: -1,
        zIndex: 2,
        resting: { x: '0cqw', y: '0cqw', rotate: -7.65 },
        hover: { x: '-1.667cqw', y: '-0.238cqw', rotate: -8.85 },
    },
    {
        ...CENTER_CARD,
        src: `${ASSET_BASE}/skateboarding.webp`,
        alt: 'Skateboarder',
        side: 0,
        zIndex: 3,
        resting: { x: '0cqw', y: '0cqw', rotate: 0, scale: 1 },
        hover: { x: '0cqw', y: '-0.714cqw', rotate: 0, scale: 1.015 },
    },
    {
        ...INNER_CARD,
        src: `${ASSET_BASE}/mountain_range.webp`,
        alt: 'Mountain range',
        side: 1,
        zIndex: 2,
        resting: { x: '0cqw', y: '0cqw', rotate: 7.65 },
        hover: { x: '1.667cqw', y: '-0.238cqw', rotate: 8.85 },
    },
    {
        ...OUTER_CARD,
        src: `${ASSET_BASE}/jellyfish.webp`,
        alt: 'Jellyfish',
        side: 1,
        zIndex: 1,
        resting: { x: '0cqw', y: '0cqw', rotate: 14.39 },
        hover: { x: '3.333cqw', y: '0.714cqw', rotate: 16.89 },
    },
];

/* ——— AnimatedCardDisplay ——————————————————————————————————————————————————— */

export function AnimatedCardDisplay(): JSX.Element {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div className="w-full flex justify-center">
            <div
                className="@container relative w-full sm:w-[18.75rem] lg:w-[26.25rem] 3xl:w-[46.25rem] 4xl:w-[59rem]"
                style={{ aspectRatio: `${DESIGN_FRAME.width} / ${DESIGN_FRAME.height}` }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {CARDS.map((card) => (
                    <CardLayer key={card.src} card={card} isHovered={isHovered} frame={DESIGN_FRAME} />
                ))}
            </div>
        </div>
    );
}
